import React, { useState, useEffect, useContext } from 'react';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Text,
  Modal,
  Button,
  Link,
  Notification,
} from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { useAsyncFn } from 'react-use';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { fetchPlanName } from 'utils/format';
import { trackClickEvent } from 'domain/tracking';
import { useAccountContext } from 'contexts/account';
import {
  ReactivatePlanModalHeader,
  SVGHolder,
  MoversSubModal,
} from './modal.style';
import greenHome from '/images/icons/green-home.svg';
import { REACTIVATE_PLAN } from './modal.api';
import { REACTIVATION_SOURCE } from './modal.constants';

const getCopy = copyFor('modules.reactivatePlan');

interface Props {
  onClose: (e: any) => void;
  planNickname: string;
  planId: number;
  source?: REACTIVATION_SOURCE;
}

const triggerSegmentEvent = (planId, source, response) => {
  trackClickEvent(`planReactivation`, {
    metadata: {
      planId,
      source,
      clicked: 'plan_reactivated',
      response: response ? 'success' : 'failure',
    },
  });
};

export const ReactivatePlan = ({
  onClose,
  planNickname,
  planId,
  source,
}: Props) => {
  const history = useHistory();
  const { customerType, userOriginationSource } = useAccountContext();
  const { addNotification } = useContext(Notification.Context);
  const { movingSelectAccount } = routes();
  const [funnel, setFunnel] = useState(userOriginationSource);
  const [mutate] = useMutation(REACTIVATE_PLAN);
  const [showMoversSubModal, setShowMoversSubModal] = useState(true);

  useEffect(() => {
    setFunnel(userOriginationSource);
  }, [userOriginationSource]);

  useEffect(() => {
    if (customerType === 'smb' || funnel === 'business') {
      setShowMoversSubModal(false);
    }
  }, [customerType, funnel]);

  const planName = fetchPlanName(planNickname, planId);

  const [
    { loading: mutationLoading, error: mutationError, value: mutationData },
    reactivatePlan,
  ] = useAsyncFn(async () => {
    const { data } = await mutate({
      variables: { id: planId },
    });

    const { reactivatePlan: response } = data;
    if (!mutationLoading && response?.success) {
      onClose({ refreshDashboard: response.success });

      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message: getCopy('reactivationSuccess'),
        variant: 'success',
      });
    }
    triggerSegmentEvent(planId, source, response.success);
    return data;
  }, [mutate, planId]);

  return (
    <Modal aria-label="Reactivate Plan" size="small">
      <ReactivatePlanModalHeader title={getCopy('title')} onClose={onClose} />
      <Modal.Content>
        <Text
          margin={{ bottom: '16px' }}
          textStyle="paragraph500"
          className="notranslate"
        >
          {getCopy('description', { planName })}
        </Text>
        <Text
          margin={{ bottom: '16px' }}
          textStyle="paragraph400"
          opacity="low"
        >
          {getCopy('modalDescription')}
        </Text>
        {showMoversSubModal && (
          <MoversSubModal>
            <SVGHolder
              src={greenHome}
              style={{ marginBottom: '16px' }}
              aria-label="Green Home"
            />
            <Text margin={{ bottom: '8px' }} textStyle="heading500">
              {getCopy('movers.title')}
            </Text>
            <Text
              margin={{ bottom: '16px' }}
              textStyle="paragraph400"
              opacity="high"
            >
              {getCopy('movers.description')}
            </Text>
            <Link
              color="primary"
              backgroundless={true}
              onClick={() => {
                history.push(movingSelectAccount);
              }}
              type="CTA"
              size="small"
              openInNewTab={false}
            >
              {getCopy('movers.movingHome')}
            </Link>
          </MoversSubModal>
        )}
        {(mutationError || (mutationData && !mutationData.success)) && (
          <Alert margin={{ bottom: '16px' }} variant="error">
            {getCopy('reactivationFailure')}
          </Alert>
        )}
        <Button
          backgroundColor="primaryInverse"
          fullWidth
          loading={mutationLoading}
          onClick={reactivatePlan}
        >
          {getCopy('planFunction')}
        </Button>
      </Modal.Content>
    </Modal>
  );
};
